import { Section } from '@/_shared/components/section/index.js';
import { tokens } from '@fluentui/react-components';
import { MailInboxRegular } from '@fluentui/react-icons';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type Row,
    type SortingState,
    getFilteredRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { ResizableTableRowHeader } from '../resizable-table/components/row-header/index.js';
import { useResizableTable } from '../resizable-table/hooks/index.js';
import { ResizableTableVirtualized } from '../resizable-table/index.js';
import { DataTableToolbarCount } from './toolbar/count/index.js';
import type { DataTableExport } from './toolbar/export/hooks.js';
import { DataTableToolbar } from './toolbar/index.js';

export type DataTableProps<TData, TValue> = {
    id: string;
    tableLabel?: string;
    tableLabelNode?: ReactNode;
    loading?: boolean;
    data?: TData[];
    columns: ColumnDef<TData, TValue>[];
    getItemId: (item: TData) => string;
    columnTitles: Partial<Record<keyof TData, string>>;
    defaultSorting?: SortingState;
    defaultFilters?: ColumnFiltersState;
    customToolbarActions?: ReactNode;
    generateCustomToolbarCounts?: (data: Row<TData>[]) => { label: string; count: number }[];
    dataExportFunc?: (data: TData[]) => DataTableExport;
    // onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
    onRowSelectionChange?: (updateValues: string[]) => void;
};

/**
 * A table component for displaying data in a tabular format.
 *
 * @param id The unique identifier of the table.
 * @param tableLabel The label to be displayed in the table header.
 * @param loading A flag indicating whether the table is loading data.
 * @param data The data to be displayed in the table.
 * @param getItemId A function that returns the unique identifier of a data item.
 * @param columns The columns to be displayed in the table.
 * @param columnTitles The titles to be displayed in the table header.
 * @param defaultSorting The default sorting state of the table.
 * @param defaultFilters The default filters to be applied to the table.
 * @param customToolbarActions Custom actions to be displayed in the table toolbar.
 * @param dataExport The export configuration for the table.
 * @param onRowSelectionChange A callback that is called when the row selection changes.
 * @param tableLabel The label to be displayed in the table header.
 * @returns The rendered DataTable component.
 */
export function DataTable<TData, TValue>({
    id,
    tableLabel,
    tableLabelNode,
    loading,
    data: dataOrUndefined,
    columns,
    getItemId,
    columnTitles,
    defaultSorting,
    defaultFilters,
    customToolbarActions,
    generateCustomToolbarCounts,
    dataExportFunc,
    // onColumnFiltersChange,
    onRowSelectionChange,
}: DataTableProps<TData, TValue>): JSX.Element {
    const data = useMemo(() => dataOrUndefined || [], [dataOrUndefined]);

    const { table, resetTableLayout, context, columnOrder, columnSizes, setColumnSizes } = useResizableTable({
        id: `data-table-${id}`,
        columns,
        data,
        getItemId,
        filter: {
            default: defaultFilters,
            getModel: getFilteredRowModel,
        },
        selection: {
            enabled: true,
            onChange: onRowSelectionChange,
        },
        sort: {
            default: defaultSorting,
            getModel: getSortedRowModel,
        },
    });

    const dataExportWrapperFunc = useCallback(() => {
        if (!dataExportFunc) {
            return null;
        }

        const filteredData = table.getRowModel().rows.map((row) => row.original);

        return dataExportFunc(filteredData);
    }, [dataExportFunc, table]);

    const FixedHeaderContent = useCallback(
        () => (
            <ResizableTableRowHeader
                headerGroups={table.getHeaderGroups()}
                columnSizes={columnSizes}
                setColumnSizes={setColumnSizes}
                backgroundColor={tokens.colorNeutralBackground1}
                padContainerValue={context.padContainerValue}
            />
        ),
        [table, context.padContainerValue, columnSizes, setColumnSizes]
    );

    return (
        <Section
            fillContainer
            header={{
                icon: <MailInboxRegular />,
                label: tableLabelNode ?? tableLabel,
                actions: (
                    <DataTableToolbar
                        tableId={id}
                        table={table}
                        resetTableLayout={resetTableLayout}
                        columnTitles={columnTitles}
                        columnOrder={columnOrder}
                        customActions={customToolbarActions}
                        dataExportFunc={dataExportFunc ? dataExportWrapperFunc : undefined}
                    />
                ),
            }}
            footer={
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                    <DataTableToolbarCount table={table} generateCustomCounts={generateCustomToolbarCounts} />
                </div>
            }
            childrenArray={[
                {
                    removeContentPadding: true,
                    showGradient: false,
                    element: (
                        <ResizableTableVirtualized
                            context={context}
                            table={table}
                            data={data}
                            loading={loading}
                            FixedHeaderContent={FixedHeaderContent}
                        />
                    ),
                },
            ]}
        />
    );
}
